import axios, { AxiosInstance, InternalAxiosRequestConfig } from "axios";

import { BASE_URL_AUTH_API } from "../../config";
import { buildLoginRedirectUrl, isPublicAuthRoute } from "../../commons/helpers";
import TokenManager from "../../commons/TokenManager";
import { refreshToken } from "./auth";

type RetryConfig = InternalAxiosRequestConfig & {
    _retry?: boolean;
};

function createAPI(api: AxiosInstance) {
    api.interceptors.request.use((config: RetryConfig) => {
        const newConfig = { ...config };
        newConfig.headers.Authorization = `Bearer ${TokenManager.getAccessToken()}`;
        return newConfig;
    });

    api.interceptors.response.use(
        (response) => response,
        async (error) => {
            if (!error.response) {
                return Promise.reject(error);
            }

            const config = error.config as RetryConfig;
            const status = error.response.status;

            if (config._retry) {
                return Promise.reject(error);
            }

            const refreshAccessToken = TokenManager.getRefreshToken();

            if (refreshAccessToken && status === 401) {
                config._retry = true;

                try {
                    const { data } = await refreshToken({
                        refreshToken: refreshAccessToken,
                    });
                    TokenManager.setAccessToken(data.accessToken, data.refreshToken);

                    config.headers.Authorization = `Bearer ${TokenManager.getAccessToken()}`;

                    return await axios(config);
                } catch (err) {
                    TokenManager.clear();
                    if (!isPublicAuthRoute(window.location.pathname)) {
                        window.location.href = buildLoginRedirectUrl();
                    }
                    return Promise.reject(err);
                }
            }
            return await Promise.reject(error);
        }
    );

    return api;
}

const authApi = axios.create({ baseURL: BASE_URL_AUTH_API });

export const authAPI = createAPI(authApi);

export const publicAuthAPI = axios.create({
    baseURL: BASE_URL_AUTH_API,
});
